/**
 * Copyright (C) Siemens AG 1996-2026 ALL RIGHTS RESERVED.
 */

import {Component, inject, Input} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule} from '@ngx-translate/core';
import {
  MultiLanguageProperty,
  SubmodelElement,
} from '@product-passport/dpp/model';
import {TranslateOrFallbackPipe} from '../../../dpp-submodel-field/utils/translate-or-fallback.pipe';
import {DppFieldLabelComponent} from '../field-label/field-label.component';

/**
 * All BCP 47 language tags supported by the Intl API.
 * Labels are resolved via Intl.DisplayNames in English.
 */
const ALL_LANGUAGES: {label: string; value: string}[] = (() => {
  const displayNames = new Intl.DisplayNames(['en'], {type: 'language'});
  const tags = [
    'af',
    'sq',
    'am',
    'ar',
    'hy',
    'as',
    'az',
    'eu',
    'be',
    'bn',
    'bs',
    'bg',
    'my',
    'ca',
    'cs',
    'zh',
    'hr',
    'da',
    'nl',
    'en',
    'et',
    'fo',
    'fi',
    'fr',
    'gl',
    'ka',
    'de',
    'el',
    'gu',
    'he',
    'hi',
    'hu',
    'is',
    'id',
    'ga',
    'it',
    'ja',
    'kn',
    'kk',
    'km',
    'ko',
    'lo',
    'lv',
    'lt',
    'mk',
    'ms',
    'ml',
    'mt',
    'mr',
    'mn',
    'ne',
    'nb',
    'nn',
    'or',
    'ps',
    'fa',
    'pl',
    'pt',
    'pa',
    'ro',
    'ru',
    'sr',
    'sk',
    'sl',
    'es',
    'sw',
    'sv',
    'ta',
    'te',
    'th',
    'tr',
    'uk',
    'ur',
    'uz',
    'vi',
    'cy',
    'zu',
  ];
  return tags
    .map((tag) => ({
      label: displayNames.of(tag) ?? tag,
      value: tag,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
})();

@Component({
  selector: 'app-multi-language-property',
  templateUrl: './multi-language-property.component.html',
  styleUrl: './multi-language-property.component.scss',
  imports: [
    IxModule,
    TranslateModule,
    ReactiveFormsModule,
    DppFieldLabelComponent,
    TranslateOrFallbackPipe,
  ],
})
export class MultiLanguagePropertyComponent {
  private readonly fb = inject(FormBuilder);

  @Input({required: true}) element!: SubmodelElement;
  @Input({required: true}) formGroup!: FormGroup;

  get mlp(): MultiLanguageProperty {
    return this.element as MultiLanguageProperty;
  }

  readonly allLanguages = ALL_LANGUAGES;

  get usedLanguages(): string[] {
    return Object.keys(this.formGroup.controls);
  }

  private get realUsedLanguages(): string[] {
    return this.usedLanguages.filter((l) => !this.isPending(l));
  }

  availableLanguages(): {label: string; value: string}[] {
    return this.allLanguages.filter(
      (lang) => !this.realUsedLanguages.includes(lang.value),
    );
  }

  getLanguageOptionsForRow(
    currentLang: string,
  ): {label: string; value: string}[] {
    return this.allLanguages.filter(
      (lang) =>
        lang.value === currentLang ||
        !this.realUsedLanguages.includes(lang.value),
    );
  }

  isPending(lang: string): boolean {
    return lang.startsWith('__pending_');
  }

  addLanguage(): void {
    const pendingKey = `__pending_${Date.now()}`;
    this.formGroup.addControl(pendingKey, this.fb.control(''));
  }

  removeLanguage(lang: string): void {
    this.formGroup.removeControl(lang);
  }

  changeLanguage(oldLang: string, newValue: string | string[]): void {
    const newLang = Array.isArray(newValue) ? (newValue[0] ?? '') : newValue;
    if (!newLang || newLang === oldLang) {
      return;
    }
    const currentValue = this.formGroup.get(oldLang)?.value ?? '';
    this.formGroup.removeControl(oldLang);
    this.formGroup.addControl(newLang, this.fb.control(currentValue));
  }
}
